import { useState } from 'react';
import { usePopper } from 'react-popper';
import { Popover, PopoverPanel } from '@headlessui/react';
import { useTranslation } from '@/helpers/translate';
import { tt } from '@/helpers/spacePublicSimulator';

function PriceByPeriod({ details, spaceRef }) {
  const [open, setOpen] = useState();
  const [refLabel, setRefLabel] = useState();
  const [refPopover, setRefPopover] = useState();
  const { styles, attributes } = usePopper(refLabel, refPopover, { placement: 'top' });
  const { t } = useTranslation();

  const byPersonItem = (details?.priceByPeriod || []).find((p) => p?.byYieldId === 'byperson');
  const byPersonNote = byPersonItem
    ? tt({
      t,
      key: 'space:simulator.byperson_suffix',
      params: {
        counter: byPersonItem?.counter ?? 0,
        amount: byPersonItem?.amount ?? 0,
      },
      fallback: `(dont ${byPersonItem?.counter ?? 0} pers. × ${byPersonItem?.amount ?? 0}€ / jour)`,
    })
    : '';

  /*
    WEA-163 - une ligne horaire ne se lit pas « par jour » : elle porte le montant d'une
    date reservee a l'heure. Le suffixe devient donc « pour 3 h », ou « a l'heure » quand
    le groupe melange des durees (le moteur n'en remonte alors aucune, plutot qu'une
    fausse).
  */
  function suffixFor(p) {
    if (p?.byYieldId === 'hourly') {
      // `hoursEach` = duree d'UNE date du groupe. Ne pas utiliser `hours`, qui est le
      // cumul du groupe (2 dates de 3 h y valent 6) : le libelle serait faux des que le
      // groupe contient plusieurs dates.
      if (p?.hoursEach > 0) {
        return tt({
          t,
          key: 'space:simulator.hourly_suffix',
          params: { hours: p.hoursEach },
          fallback: `pour ${p.hoursEach} h`,
        });
      }
      return tt({
        t,
        key: 'space:simulator.hourly_suffix_any',
        fallback: "à l'heure",
      });
    }

    // La « nuit » (period 3) n'est plus commercialisee : plus de libelle dedie.
    let text = '';
    if (p?.period === 1 || p?.period === 2) {
      text = t('extra:formule.half');
    } else if (p?.period === 0) {
      text = t('space:formules.day');
    }

    // Suffixe journalier historique : le « par » reste dans le JSX, inchangé.
    return text;
  }

  function getDetailPrice(full) {
    return (details?.priceByPeriod || [])
      .filter((p) => p?.byYieldId !== 'byperson')
      .map((p) => {
        if (!p.total) return null;
        const isHourly = p?.byYieldId === 'hourly';
        const text = suffixFor(p);
        const priceperiod = p.amount;
        return (
          <div className="w-full" key={`searchresultsspacebyperioddetails${spaceRef}${full}${text}${priceperiod}`}>
            {full ? (
              <div className="flex justify-between">
                <div>
                  <span className="font-medium text-purple">
                    {p.counter}
                    {' '}
                    x
                    {' '}
                  </span>
                  {p.amount}
                  €
                  <span className="text-purple-5/50">
                    {' '}
                    {isHourly ? text : (
                      <>
                        par
                        {' '}
                        {text}
                      </>
                    )}
                  </span>
                </div>
                <div className="font-medium">
                  {p.total}
                  €
                </div>
              </div>
            ) : (
              <div>
                <span className="font-medium">
                  {p.amount}
                  €
                </span>
                <span className="font-medium text-purple-5/50">{text}</span>
              </div>
            )}
          </div>
        );
      });
  }

  function getDetails() {
    const contentPrice = getDetailPrice(true);
    return (
      <div className="px-4 py-4 bg-white border rounded-lg border-purple-5/10 shadow-button">
        {contentPrice}
        {byPersonNote && (
          <div className="text-purple-5/60 text-xs italic pt-1">
            {byPersonNote}
          </div>
        )}
        {(() => {
          if (details?.riderFees?.excl_tax > 0) {
            return (
              <div className="flex justify-between pt-1 text-dark">
                <span>{t('search:fees')}</span>
                <span>
                  {details?.riderFees?.excl_tax}
                  €
                </span>
              </div>
            );
          }

          return null;
        })()}
        <div className="flex justify-between pt-2 mt-2 border-t border-purple-4/20">
          <span>{t('search:total')}</span>
          <span className="font-medium">
            {details?.riderOwing?.excl_tax}
            €
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Popover>
        <div
          className="mt-1 mb-2 cursor-pointer"
          ref={setRefLabel}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <span className="text-purple-5/50 pb-[0.5px] border-b border-purple-4/20">
            {(() => {
              const beforeFidelity = Number(details?.priceByDatesBeforeFidelity?.excl_tax);
              const finalPrice = Number(details?.riderOwing?.excl_tax);
              if (
                Number.isFinite(beforeFidelity)
                && Number.isFinite(finalPrice)
                && beforeFidelity > finalPrice
              ) {
                return (
                  <span className="text-gray-400 line-through mr-1 text-xs">
                    {beforeFidelity}
                    €
                  </span>
                );
              }
              return null;
            })()}
            <span className="font-medium text-purple-5">
              {details?.riderOwing?.excl_tax}
              €
              {' '}
            </span>
            {t('search:total')}
          </span>
        </div>
        {!open
        || (
        <PopoverPanel
          className="min-w-[320px] mb-2 absolute z-60"
          static
          ref={setRefPopover}
          style={styles.popper}
          {...attributes.popper}
        >
          {getDetails()}
        </PopoverPanel>
        )}
      </Popover>
    </div>
  );
}

export default PriceByPeriod;
